import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';
import {
  buildSortPlansPayloads,
  loanInputsFromSummary,
  type SortPlansPayload,
  type SortedPlanRow,
} from 'tio-common/utils/strategy-finder-0726';
import {
  getDemoDebtLevel,
  isFirstLoginPersona,
  isPslfEligibleClient,
  type DemoSessionSelectionSnapshot,
} from 'tio-employee/utils/career-pathways-demo-data';
import { USER_ID } from 'tio-employee/demo/identity-fixtures';
import { requestingAppIs } from 'tio-employee/demo/types';
import {
  buildDemoLoanSummary,
  DEMO_FEDERAL_LOAN_TIME,
  DEMO_PSLF_QUALIFYING_PAYMENTS,
  demoHousehold,
  demoPslfActive,
  type DemoHousehold,
} from 'tio-employee/demo/loan-profile';
import { buildSortPlansOutput, simulateSchedule } from 'tio-employee/demo/repayment-plan-simulator';
import { buildIdr0726Payloads } from 'tio-employee/utils/idr-dashboard-0726';
import { buildFe0726Payloads } from 'tio-employee/utils/forgiveness-estimator-0726';

export const DEMO_LOAN_REC_IDS = {
  idr0726Latest: 'demo-loan-rec-idr-0726-2',
  idr0726Earlier: 'demo-loan-rec-idr-0726-1',
  idrLegacy: 'demo-loan-rec-idr-legacy-1',
  sf0726: 'demo-loan-rec-sf-0726-1',
  pslf0726: 'demo-loan-rec-pslf-0726-1',
  pslfLegacy: 'demo-loan-rec-pslf-legacy-1',
};

const PSLF_0726_CREATED_AT = '2026-06-15T14:00:00.000Z';
const PSLF_RUN_CREATED_AT = '2026-09-02T15:00:00.000Z';
const PSLF_LEGACY_CREATED_AT = '2026-05-20T14:00:00.000Z';

const ECHO_SLUGS: Record<string, string> = {
  'IDR-0726': 'idr-0726',
  'SF-0726': 'sf-0726',
  'PSLF-0726': 'pslf-0726',
};

const SUMMARY_URL_RE = new RegExp(`/users/${USER_ID}/summarize-loans(?:\\?|$)`);
const GENERATE_URL_RE = /\/api\/v2\/payment-plan-explorer\/generate(?:\?|$)/;
const SINGLE_URL_RE = /\/recommendations\/(demo-loan-rec-[a-z0-9-]+)(?:\?|$)/;
const COLLECTION_URL_RE = /\/recommendations\?/;
const CREATE_URL_RE = /\/recommendations(?:\?|$)/;
const ANSWER_PROFILES_URL_RE = /\/answer-profiles\?/;
const DEMO_USER_FILTER_RE = new RegExp(`filter(?:%5B|\\[)user(?:%5D|\\])=${USER_ID}(?:&|$)`);
const RUN_ID_RE = /^demo-loan-rec-run-(idr-0726|sf-0726|pslf-0726)-/;

const isDemoUserFilter = (url: string) => DEMO_USER_FILTER_RE.test(url);

let submitSeq = 0;

// A PSLF session's borrower is on IBR; every other session keeps the debt level's plan.
export function demoSessionLoanSummary(
  selection: DemoSessionSelectionSnapshot,
  debtLevelId = selection.debtLevelId
) {
  return buildDemoLoanSummary(debtLevelId, demoPslfActive(selection));
}

function sessionHousehold(
  selection: DemoSessionSelectionSnapshot,
  debtLevelId = selection.debtLevelId
): DemoHousehold {
  const household = demoHousehold(debtLevelId);
  return demoPslfActive(selection)
    ? { ...household, madePayments: DEMO_PSLF_QUALIFYING_PAYMENTS }
    : household;
}

export function demoPslfHistoryVisible(selection: DemoSessionSelectionSnapshot) {
  return (
    demoPslfActive(selection) &&
    selection.pslfScenarioId === 'full-journey' &&
    demoLoanHistoryVisible(selection)
  );
}

export function demoLoanHistoryVisible(selection: DemoSessionSelectionSnapshot) {
  return (
    getDemoDebtLevel(selection.debtLevelId).hasLinkedLoans &&
    !isFirstLoginPersona(selection.userScenarioId)
  );
}

// Saved runs always show a linked loan, so a not-linked session reads them at the medium level.
function recordProfile(selection: DemoSessionSelectionSnapshot) {
  const level = getDemoDebtLevel(selection.debtLevelId).hasLinkedLoans
    ? selection.debtLevelId
    : 'medium';
  return {
    household: sessionHousehold(selection, level),
    loanInputs: loanInputsFromSummary(demoSessionLoanSummary(selection, level)),
  };
}

function idrModel(household: DemoHousehold, agi = household.agiCents) {
  return {
    agi,
    numberOfAdults: household.numberOfAdults,
    numberOfChildren: household.numberOfChildren,
    firstLoanTime: household.firstLoanTime,
    lastLoanTime: household.lastLoanTime,
    madePayments: household.madePayments,
    affordPayments: household.affordPayments,
  };
}

function calculation(data: SortPlansPayload[], requestingApp: string) {
  return {
    calculationInput: { data },
    calculationOutput: { data: data.map((row) => buildSortPlansOutput(row, requestingApp)) },
  };
}

function recommendation(id: string, attributes: Record<string, unknown>): JsonApiResource {
  return { type: 'recommendation', id, attributes: { hasErrorInCalcs: false, ...attributes } };
}

// An earlier run's income, 94% of today's.
const earlierAgi = (household: DemoHousehold) =>
  Math.round((household.agiCents * 0.94) / 100) * 100;

function idr0726Record(selection: DemoSessionSelectionSnapshot, earlier: boolean) {
  const { household, loanInputs } = recordProfile(selection);
  const agi = earlier ? earlierAgi(household) : household.agiCents;
  return recommendation(
    earlier ? DEMO_LOAN_REC_IDS.idr0726Earlier : DEMO_LOAN_REC_IDS.idr0726Latest,
    {
      requestingApp: 'IDR-0726',
      createdAt: earlier ? '2026-07-14T15:00:00.000Z' : '2026-09-02T15:00:00.000Z',
      ...calculation(buildIdr0726Payloads(idrModel(household, agi), loanInputs), 'IDR-0726'),
    }
  );
}

// tio-api LegacyStrategyFinderGeneratorV2 copy from config/locales/en.yml, 'noreferer' typo included.
const IDR_FORM_LINK =
  "<a href='https://studentaid.gov/app/ibrInstructions.action' target='_blank' rel='noreferer noopener'>";

const incomeDrivenSteps = (title: string) => [
  `Submit the ${IDR_FORM_LINK}Income-Driven Repayment Plan Request Form</a>.`,
  'Your servicer will automatically update your payment amount but it can take some time. You can check on the status of your application by contacting your servicer.',
  `${title} needs to be renewed every year. Be sure to ${IDR_FORM_LINK}update your income and family size</a> annually to stay on this plan.`,
];

const LEGACY_PLAN_TITLES: Record<string, string> = {
  ibr: 'IBR',
  paye: 'PAYE',
  icr: 'ICR',
  tenYearsStandard: '10 Year Standard Repayment Plan',
};

// Next steps exist only for a best plan, which the legacy engines picked from these.
const LEGACY_NEXT_STEPS: Record<string, { eligibleLoanTypes: string; subtitle: string }> = {
  ibr: {
    eligibleLoanTypes:
      'All Direct and FFEL loans except Parent PLUS loans or Consolidation Loans used to repay Parent PLUS loans',
    subtitle: 'How to switch to the Income Based Repayment plan',
  },
  paye: {
    eligibleLoanTypes:
      'Borrowers who took out their first student loan after Oct 1 2007 and are looking for income-driven repayment',
    subtitle: 'How to switch to the Pay As You Earn repayment plan',
  },
  icr: {
    eligibleLoanTypes:
      'All Direct Loans Parent PLUS and FFEL Loans if consolidated to a Direct Consolidation Loan',
    subtitle: 'How to switch to the Income Contingent Repayment plan',
  },
};

// The legacy engines recommended PAYE, SAVE, IBR or ICR; the demo takes the lowest payment, as the simulator has no SAVE.
const LEGACY_IDR_CHOICES = Object.keys(LEGACY_NEXT_STEPS);
const LEGACY_DIRECT_HEADING = 'Direct loans:';
const LEGACY_ACADEMIC_DEGREE = 'undergradGrad';
const NOTHING_LEFT_TO_FORGIVE =
  'Calculator indicates there will be nothing left to forgive at the end of this payment plan.';

// tio-api sums each group's amounts as floats and serializes them with Ruby's Float#to_s.
const legacyCents = (cents: number) => `${Math.round(cents)}.0`;

const dasherizeKeys = (summary: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(summary).map(([key, value]) => [key.replace(/_/g, '-'), value])
  );

const IDLF = 'IncomeDrivenLoanForgiveness';
const PSLF_ADDITION = 'PublicServiceLoanForgiveness';
const NOTHING_LEFT = 'NothingLeftToForgive';

// tio-api's PSLFv2 data style adds PSLF to the current plan unless it is one of these or no payments were made.
const NON_INCOME_DRIVEN_PLANS = [
  'tenYearsStandard',
  'extendedPlan',
  'refinancing',
  'graduated',
  'noRepaymentPlan',
];

const PSLF_FORM_LINK =
  "<a href='https://studentaid.gov/sites/default/files/public-service-application-for-forgiveness.pdf' target='_blank' rel='noreferer noopener'>PSLF Form</a>";

// get_switching_instructions(:publicServiceLoanForgiveness, 'recommended'), from the same locale file.
const PSLF_STEPS = {
  title: 'PSLF',
  subtitle: 'How to qualify for Public Service Loan Forgiveness',
  instructions: [
    "Make sure you're on the recommended repayment plan.",
    `Make sure you're in eligible employment by completing & submitting the ${PSLF_FORM_LINK}. Track your progress toward PSLF by submitting your PSLF form at least annually.`,
    'After making 120 months of eligible payments submit your final PSLF form. While you wait for approval be sure to continue making your minimum monthly payments to remain eligible for forgiveness.',
  ],
};

// The IDR data style adds IDLF to every current plan; PSLFv2 adds PSLF to an income-driven one.
function legacyCurrentProgram(pslf: boolean, input: SortPlansPayload) {
  if (!pslf) {
    return IDLF;
  }
  return NON_INCOME_DRIVEN_PLANS.includes(input.current_plan ?? '') ||
    input.current_paid_months <= 0
    ? null
    : PSLF_ADDITION;
}

// tio-user-profile appends NothingLeftToForgive when a plan's forgiveness program forgives nothing.
function legacyAdditions(program: string | null, row: SortedPlanRow) {
  if (!program) {
    return [];
  }
  return row.forgiveness_paid <= 0 ? [program, NOTHING_LEFT] : [program];
}

// The matched plan types' titles in tio-api's plan_types order; NothingLeftToForgive has no title.
function legacyHeadings(title: string, additions: string[], current: boolean) {
  const nothingLeft = additions.includes(NOTHING_LEFT);
  // tio-api drops PSLF from a current plan that has nothing left to forgive.
  const pslf = additions.includes(PSLF_ADDITION) && !(current && nothingLeft);
  const bullets = [title, ...(pslf ? [PSLF_STEPS.title] : []), ...(nothingLeft ? [null] : [])];
  return [{ title: LEGACY_DIRECT_HEADING, bullets }];
}

function legacyAmounts(row: SortedPlanRow, balanceCents: number) {
  return {
    payoff_amount: [legacyCents(row.total_payment)],
    total_interest_paid: [legacyCents(row.total_payment + row.forgiveness_paid - balanceCents)],
    estimated_amount_forgiven: [legacyCents(row.forgiveness_paid)],
    term_months: [row.num_payments],
  };
}

// The legacy form sent only these answers, and asked about affordability for IDR alone.
function legacyInputSummary(input: SortPlansPayload | undefined, pslf: boolean) {
  const answered = input && {
    'household-income': input.agi,
    'household-size': input.number_of_adults + input.number_of_children,
    'made-payments': input.current_paid_months,
    'loan-type': input.loan_type,
    'federal-loan-time': DEMO_FEDERAL_LOAN_TIME,
    'academic-degree': LEGACY_ACADEMIC_DEGREE,
    ...(pslf ? {} : { 'afford-payments': input.afford_payments }),
  };
  return {
    'household-income': null,
    'household-size': null,
    'current-working': null,
    'forgiveness-program': null,
    'made-payments': null,
    'loan-type': null,
    'federal-loan-time': null,
    'employment-type': null,
    'plan-in-gov': null,
    'plan-as-teacher': null,
    'one-time-adj-payments': null,
    'academic-degree': null,
    'afford-payments': null,
    'payoff-goal': null,
    'residence-state': null,
    ...answered,
  };
}

function legacyRecommendationFields(
  input: SortPlansPayload | undefined,
  plans: SortedPlanRow[],
  pslf: boolean
): Record<string, unknown> {
  const currentProgram = input ? legacyCurrentProgram(pslf, input) : null;
  const best = plans
    .filter(
      (row) => LEGACY_IDR_CHOICES.includes(row.plan) && row.pslf === pslf && row.tlf_amount === 0
    )
    .reduce<SortedPlanRow | undefined>(
      (low, row) =>
        !low ||
        row.first_month_payment < low.first_month_payment ||
        (row.first_month_payment === low.first_month_payment &&
          row.total_payment < low.total_payment)
          ? row
          : low,
      undefined
    );
  const current = plans.find(
    (row) =>
      row.plan === input?.current_plan &&
      row.pslf === (currentProgram === PSLF_ADDITION) &&
      row.tlf_amount === 0
  );
  const bestTitle = best && LEGACY_PLAN_TITLES[best.plan];
  const bestSteps = best && LEGACY_NEXT_STEPS[best.plan];
  const currentTitle = current && LEGACY_PLAN_TITLES[current.plan];
  if (!input || !best || !bestTitle || !bestSteps || !current || !currentTitle) {
    return {
      hasErrorInCalcs: true,
      bestPlanSummary: {},
      currentPlanSummary: {},
      bestPlanTableHeadings: [],
      currentPlanTableHeadings: [],
      bestPlanNextSteps: [],
      inputSummary: legacyInputSummary(undefined, pslf),
      outputSummary: {},
    };
  }
  const balance = input.current_balance;
  const bestAdditions = legacyAdditions(pslf ? PSLF_ADDITION : IDLF, best);
  const monthly = [...new Set([best.first_month_payment, best.max_monthly_payment])]
    .sort((a, b) => a - b)
    .map(legacyCents);
  const recommended = {
    headings: legacyHeadings(bestTitle, bestAdditions, false),
    ...(monthly.length === 2 ? { monthly_payment_first_year: monthly[0] } : {}),
    monthly_payments: monthly,
    ...legacyAmounts(best, balance),
  };
  // tio-api replaces the current plan's payment with the one the estimator submitted from the loan summary.
  const currentSummary = {
    headings: legacyHeadings(currentTitle, legacyAdditions(currentProgram, current), true),
    monthly_payments: [legacyCents(input.monthly_payment)],
    ...legacyAmounts(current, balance),
  };
  const steps = {
    title: bestTitle,
    subtitle: bestSteps.subtitle,
    instructions: incomeDrivenSteps(bestTitle),
  };
  const pslfSteps = bestAdditions.includes(PSLF_ADDITION) ? [PSLF_STEPS] : [];
  return {
    bestPlanSummary: dasherizeKeys(recommended),
    currentPlanSummary: dasherizeKeys(currentSummary),
    bestPlanTableHeadings: recommended.headings,
    currentPlanTableHeadings: currentSummary.headings,
    bestPlanNextSteps: [steps, ...pslfSteps],
    inputSummary: legacyInputSummary(input, pslf),
    outputSummary: {
      recommended: [recommended],
      current: [currentSummary],
      'next-steps': [
        {
          title: bestTitle,
          eligible_loan_types: bestSteps.eligibleLoanTypes,
          instructions: [steps],
          additions: bestAdditions,
        },
        ...pslfSteps.map((pslfStep) => ({
          title: pslfStep.title,
          subtitle: pslfStep.subtitle,
          instructions: [pslfStep],
        })),
        ...(bestAdditions.includes(NOTHING_LEFT) ? [{ subtitle: NOTHING_LEFT_TO_FORGIVE }] : []),
      ],
      'result-type': 'summary',
    },
  };
}

// The legacy estimators asked for whole years paid, so a legacy run carries a multiple of 12 payments.
function legacyFields(selection: DemoSessionSelectionSnapshot, pslf: boolean) {
  const { household, loanInputs } = recordProfile(selection);
  const model = idrModel(
    { ...household, madePayments: Math.floor(household.madePayments / 12) * 12 },
    earlierAgi(household)
  );
  const { calculationInput, calculationOutput } = pslf
    ? calculation(buildFe0726Payloads(model, loanInputs), 'PSLF-0726')
    : calculation(buildIdr0726Payloads(model, loanInputs), 'IDR-0726');
  return legacyRecommendationFields(
    calculationInput.data[0],
    calculationOutput.data[0]?.plans ?? [],
    pslf
  );
}

function idrLegacyRecord(selection: DemoSessionSelectionSnapshot) {
  return recommendation(DEMO_LOAN_REC_IDS.idrLegacy, {
    requestingApp: 'IDR',
    createdAt: '2025-10-21T15:00:00.000Z',
    ...legacyFields(selection, false),
  });
}

function sf0726Record(selection: DemoSessionSelectionSnapshot) {
  const { household, loanInputs } = recordProfile(selection);
  const isGov = isPslfEligibleClient(selection.clientId);
  const model = {
    payoffGoal: 'slow',
    affordPayments: household.affordPayments,
    agi: household.agiCents,
    numberOfAdults: household.numberOfAdults,
    numberOfChildren: household.numberOfChildren,
    firstLoanTime: household.firstLoanTime,
    lastLoanTime: household.lastLoanTime,
    residenceState: 'OH',
    currentlyWorkingToward: 'no-but-want-learn',
    currentForgivenessProgram: '',
    madePayments: household.madePayments,
    isGov,
    isTeacher: false,
    isOther: !isGov,
    planInGov: isGov ? 'for-10-years-more' : '',
    planAsTeacher: '',
    employmentMonths: 0,
    tlfQualifies17500: false,
  };
  return recommendation(DEMO_LOAN_REC_IDS.sf0726, {
    requestingApp: 'SF-0726',
    createdAt: '2026-08-18T15:00:00.000Z',
    ...calculation(buildSortPlansPayloads(model, loanInputs), 'SF-0726'),
  });
}

function pslf0726Calculation(selection: DemoSessionSelectionSnapshot) {
  const { household, loanInputs } = recordProfile(selection);
  return calculation(buildFe0726Payloads(idrModel(household), loanInputs), 'PSLF-0726');
}

function pslf0726Record(
  selection: DemoSessionSelectionSnapshot,
  id: string,
  createdAt = PSLF_RUN_CREATED_AT
) {
  return recommendation(id, {
    requestingApp: 'PSLF-0726',
    createdAt,
    ...pslf0726Calculation(selection),
  });
}

function pslfLegacyRecord(selection: DemoSessionSelectionSnapshot) {
  return recommendation(DEMO_LOAN_REC_IDS.pslfLegacy, {
    requestingApp: 'PSLF',
    createdAt: PSLF_LEGACY_CREATED_AT,
    ...legacyFields(selection, true),
  });
}

export function buildDemoLoanRecommendations(selection: DemoSessionSelectionSnapshot) {
  return {
    idr0726Latest: idr0726Record(selection, false),
    idr0726Earlier: idr0726Record(selection, true),
    idrLegacy: idrLegacyRecord(selection),
    sf0726: sf0726Record(selection),
    pslf0726: pslf0726Record(selection, DEMO_LOAN_REC_IDS.pslf0726, PSLF_0726_CREATED_AT),
    pslfLegacy: pslfLegacyRecord(selection),
  };
}

function page(data: JsonApiResource[]): JsonApiDoc {
  return { data, meta: { page: { total: data.length } } };
}

function singleRecommendation(selection: DemoSessionSelectionSnapshot, url: string): JsonApiDoc {
  const id = SINGLE_URL_RE.exec(url)?.[1] ?? '';
  const slug = RUN_ID_RE.exec(id)?.[1];
  let record: JsonApiResource | undefined;
  if (slug === 'idr-0726') {
    record = idr0726Record(selection, false);
  } else if (slug === 'sf-0726') {
    record = sf0726Record(selection);
  } else if (slug === 'pslf-0726') {
    record = pslf0726Record(selection, id);
  } else if (id === DEMO_LOAN_REC_IDS.idr0726Latest) {
    record = idr0726Record(selection, false);
  } else if (id === DEMO_LOAN_REC_IDS.idr0726Earlier) {
    record = idr0726Record(selection, true);
  } else if (id === DEMO_LOAN_REC_IDS.idrLegacy) {
    record = idrLegacyRecord(selection);
  } else if (id === DEMO_LOAN_REC_IDS.sf0726) {
    record = sf0726Record(selection);
  } else if (id === DEMO_LOAN_REC_IDS.pslf0726) {
    record = pslf0726Record(selection, id, PSLF_0726_CREATED_AT);
  } else if (id === DEMO_LOAN_REC_IDS.pslfLegacy) {
    record = pslfLegacyRecord(selection);
  }
  return { data: record ? { ...record, id } : null };
}

function collection(selection: DemoSessionSelectionSnapshot, app: string): JsonApiDoc {
  if (app === 'PSLF-0726' || app === 'PSLF') {
    if (!demoPslfHistoryVisible(selection)) {
      return page([]);
    }
    return page([
      app === 'PSLF'
        ? pslfLegacyRecord(selection)
        : pslf0726Record(selection, DEMO_LOAN_REC_IDS.pslf0726, PSLF_0726_CREATED_AT),
    ]);
  }
  if (!demoLoanHistoryVisible(selection)) {
    return page([]);
  }
  if (app === 'IDR-0726') {
    return page([idr0726Record(selection, false), idr0726Record(selection, true)]);
  }
  if (app === 'IDR') {
    return page([idrLegacyRecord(selection)]);
  }
  if (app === 'SF-0726') {
    return page([sf0726Record(selection)]);
  }
  return page([]);
}

function answerProfile(selection: DemoSessionSelectionSnapshot): JsonApiDoc {
  const household = sessionHousehold(selection);
  const firstLogin = isFirstLoginPersona(selection.userScenarioId);
  return page([
    {
      type: 'answer-profile',
      id: 'demo-loan-answer-profile-1',
      attributes: {
        providingApp: firstLogin ? 'DASHBOARD' : 'IDR-0726',
        householdIncome: firstLogin ? null : household.agiCents,
        numberOfAdults: firstLogin ? null : household.numberOfAdults,
        numberOfChildren: firstLogin ? null : household.numberOfChildren,
        householdSize: firstLogin ? null : household.numberOfAdults + household.numberOfChildren,
        federalFirstLoanTime: firstLogin ? null : household.firstLoanTime,
        federalLastLoanTime: firstLogin ? null : household.lastLoanTime,
        madePayments: firstLogin ? null : household.madePayments,
        affordPayments: firstLogin ? null : household.affordPayments,
        recertifiedIncomeAt: null,
        changedRepaymentPlanAt: null,
        consolidatedLoansAt: null,
      },
      relationships: { user: { data: { type: 'user', id: USER_ID } } },
    },
  ]);
}

type PostedResource = {
  attributes?: Record<string, unknown>;
  relationships?: { user?: { data?: { id?: unknown } | null } };
};

function postedRecommendation(body: unknown) {
  const resource = (body as { data?: PostedResource } | null | undefined)?.data;
  const attributes = resource?.attributes;
  const app = attributes?.['requestingApp'] ?? attributes?.['requesting-app'];
  const input = (attributes?.['calculationInput'] ?? attributes?.['calculation-input']) as
    { data?: unknown } | undefined;
  const userId = resource?.relationships?.user?.data?.id;
  if (
    typeof app !== 'string' ||
    !ECHO_SLUGS[app] ||
    !Array.isArray(input?.data) ||
    (userId !== undefined && userId !== USER_ID)
  ) {
    return undefined;
  }
  return { app, rows: input.data as unknown[] };
}

function submitEcho(body: unknown): JsonApiDoc {
  const posted = postedRecommendation(body);
  if (!posted) {
    return { data: null };
  }
  const rows = posted.rows.map((row) =>
    row && typeof row === 'object' ? (row as SortPlansPayload) : ({} as SortPlansPayload)
  );
  submitSeq += 1;
  return {
    data: {
      type: 'recommendation',
      id: `demo-loan-rec-run-${ECHO_SLUGS[posted.app]}-${Date.now().toString(36)}-${submitSeq}`,
      // requestingApp and calculationInput are omitted so the submitted values survive the commit.
      attributes: {
        createdAt: new Date().toISOString(),
        hasErrorInCalcs: false,
        calculationOutput: { data: rows.map((row) => buildSortPlansOutput(row, posted.app)) },
      },
    },
  };
}

export function buildLoanEstimatorLayer(selection: DemoSessionSelectionSnapshot): {
  matchers: MatcherFixture[];
  fetchMatchers: FetchMatcher[];
} {
  const collectionMatcher = (app: string): FetchMatcher => ({
    urlMatches: (url) =>
      COLLECTION_URL_RE.test(url) && requestingAppIs(url, app) && isDemoUserFilter(url),
    method: 'GET',
    json: () => collection(selection, app),
  });
  return {
    matchers: [
      {
        matcher: { kind: 'builder', method: 'GET', urlMatches: (url) => SUMMARY_URL_RE.test(url) },
        contentFor: () => demoSessionLoanSummary(selection),
      },
      {
        matcher: {
          kind: 'builder',
          method: 'POST',
          urlMatches: (url) => GENERATE_URL_RE.test(url),
        },
        contentFor: (body) => simulateSchedule(body),
      },
    ],
    fetchMatchers: [
      {
        urlMatches: (url) => SUMMARY_URL_RE.test(url),
        method: 'GET',
        json: () => demoSessionLoanSummary(selection),
      },
      {
        urlMatches: (url) => SINGLE_URL_RE.test(url),
        method: 'GET',
        json: (url: string) => singleRecommendation(selection, url),
      },
      collectionMatcher('IDR-0726'),
      collectionMatcher('IDR'),
      collectionMatcher('SF-0726'),
      collectionMatcher('STRATEGY_FINDER'),
      collectionMatcher('PSLF-0726'),
      collectionMatcher('PSLF'),
      {
        urlMatches: (url) => ANSWER_PROFILES_URL_RE.test(url) && isDemoUserFilter(url),
        method: 'GET',
        json: () => answerProfile(selection),
      },
      {
        urlMatches: (url) => CREATE_URL_RE.test(url),
        method: 'POST',
        status: 201,
        bodyMatches: (body) => postedRecommendation(body) !== undefined,
        json: (_url: string, body?: unknown) => submitEcho(body),
      },
    ],
  };
}
